import ChevronDown from '@o2/icons/ChevronDown';
import ChevronUp from '@o2/icons/ChevronUp';
import {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  forwardRef,
  MouseEvent,
  ReactElement,
  useRef,
  useState,
} from 'react';
import { BoxProps, styled, useTheme } from '@mui/material';
import useResponsiveNavigation from './useResponsiveNavigation';
import MenuItem from './MenuItem';
import Box from '@o2/ui/Box';
import UIMenu from '@o2/ui/Menu';

export type MenuProps<T extends ElementType = 'div'> = BoxProps & {
  moreButtonText: string;
  component?: T;
  children: ReactElement<any>[];

  ref?: ComponentPropsWithRef<T>['ref'];
} & ComponentPropsWithoutRef<T>;

const StyledMenu = styled(Box, {
  name: 'AppBar',
  slot: 'Menu',
})(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  height: '100%',
  overflow: 'hidden',
}));

/**
 * Menu component expects as child direct ancestors of navigation elements. Every element is then cloned for purpose of responsive
 * navigation which could be part of navigation bar or a part of navigation under "More" button.
 *
 * Responsiveness is delivered by both `Children.map()` in its respective code blocks where first refer to horizontal navigation and
 * second to vertical navigation which is hidden under "More" button. Whether to use responsive navigation is determined by result
 * of `useResponsiveNavigation()` hook.
 */
const Menu = forwardRef(
  <T extends ElementType = 'div'>(
    { children, moreButtonText = 'o2-ui.AppBar.moreButton' }: MenuProps<T>,
    ref: MenuProps<T>['ref']
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const theme = useTheme();
    const navMoreOpen = Boolean(anchorEl);

    const menuRef = useRef<HTMLDivElement>(null);
    const { isActive, isOverlapping, intersectingTargetsCount } = useResponsiveNavigation(menuRef);

    const handleNavMoreClick = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleNavMoreClose = () => {
      setAnchorEl(null);
    };

    return (
      <StyledMenu ref={ref}>
        <Box flex={1} display="flex" overflow="hidden" flexWrap="wrap" ref={menuRef}>
          {/**
           * It is expected that first Children.map renders all possible buttons even if they are null at first.
           * `useResponsiveNavigation` expects all the buttons are rendered in `menuRef`, so it can count them and see if they are intersecting.
           * Also notice that we are setting minimal width to 0.1px. Having zero width leads to IntersectionObserver doesn't count that element as intersecting/overlapping.
           */}
          {Children.map(children, (child, i) => (
            <Box
              visibility={i < intersectingTargetsCount && isActive ? 'visible' : 'hidden'}
              display={isActive ? 'flex' : 'none'}
              height="100%"
              minWidth="0.1px"
              key={i}
              color="white"
            >
              {child &&
                cloneElement(child, {
                  ...child.props,
                })}
            </Box>
          ))}
        </Box>
        {(children?.length || 0) > intersectingTargetsCount &&
          isOverlapping &&
          isActive &&
          children.reduce((prev, acc, i) => (i >= intersectingTargetsCount ? prev || !!acc : prev), false) && (
            <Box>
              <MenuItem onClick={handleNavMoreClick}>
                {moreButtonText}{' '}
                {navMoreOpen ? <ChevronUp sx={{ fontSize: 16 }} /> : <ChevronDown sx={{ fontSize: 16 }} />}
              </MenuItem>
              <UIMenu
                anchorEl={anchorEl}
                open={navMoreOpen}
                onClose={handleNavMoreClose}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: -1,
                      py: 2,
                      backgroundColor: theme.palette.background.white,
                    },
                  },
                }}
              >
                {Children.map(
                  children,
                  (child, i) =>
                    child && (
                      <Box
                        width="100%"
                        height="40px"
                        display={i >= intersectingTargetsCount ? undefined : 'none'}
                        key={i}
                        color="white"
                      >
                        {cloneElement(child, {
                          ...child.props,
                          sx: {
                            width: '100%',
                            height: '100%',
                            ...child.props?.sx,
                          },
                        })}
                      </Box>
                    )
                )}
              </UIMenu>
            </Box>
          )}
      </StyledMenu>
    );
  }
);

Menu.displayName = 'AppBar.Menu';

export default Menu;
